/**
 * Export half of Settings → Backup & Transfer.
 *
 * The reassurance line is not decoration: users are about to write a file
 * they may email to a colleague, and the one question they will ask is
 * "does this contain my passwords?". Answering it in the UI, next to the
 * button, is part of the feature.
 */
import { useMemo, useState } from 'react';
import { Download, ShieldCheck } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from '../../../components/ui/toaster';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { buildSettingsBundle } from '../model/buildSettingsBundle';
import { SETTINGS_BUNDLE_GROUPS, type SettingsBundleGroupId } from '../model/settingsBundleManifest';
import { readStoreState } from '../services/settingsStoreTargets';
import {
  exportBundleToFile,
  listAllMetaInfo,
  METAINFO_CAVEAT,
} from '../services/settingsTransferApi';
import { SettingsGroupChecklist, type ChecklistRow } from './SettingsGroupChecklist';

const defaultSelection = () =>
  new Set<string>(SETTINGS_BUNDLE_GROUPS.filter((g) => !g.optIn).map((g) => g.id));

function suggestedFileName(): string {
  return `jam-sql-studio-settings-${new Date().toISOString().slice(0, 10)}.json`;
}

export function SettingsExportPanel() {
  const { track } = useAnalytics();
  const [selected, setSelected] = useState<Set<string>>(defaultSelection);
  const [busy, setBusy] = useState(false);

  const rows = useMemo<ChecklistRow[]>(
    () => [
      ...SETTINGS_BUNDLE_GROUPS.map((group) => ({
        id: group.id,
        label: group.label,
        description: group.description,
      })),
      {
        id: 'metaInfo' as const,
        label: 'Database meta info',
        description: METAINFO_CAVEAT,
      },
    ],
    [],
  );

  const toggle = (id: string, next: boolean) =>
    setSelected((current) => {
      const copy = new Set(current);
      if (next) copy.add(id);
      else copy.delete(id);
      return copy;
    });

  const groupIds = [...selected].filter((id) => id !== 'metaInfo') as SettingsBundleGroupId[];
  const includeMetaInfo = selected.has('metaInfo');

  const onExport = async () => {
    setBusy(true);
    try {
      const bundle = await buildSettingsBundle({
        groupIds,
        includeMetaInfo,
        sources: { readStore: readStoreState, listMetaInfo: listAllMetaInfo },
      });
      const result = await exportBundleToFile({
        json: `${JSON.stringify(bundle, null, 2)}\n`,
        suggestedFileName: suggestedFileName(),
      });

      if (!result.success) {
        if (result.cancelled) return;
        toast.error({ title: 'Export failed', description: result.error });
        return;
      }
      // Fired on success, never on click — mirrors `feature:export_results`.
      track('feature:settings_export', {
        groups: [...selected].sort().join(','),
      });
      toast.success({
        title: 'Settings exported',
        description: includeMetaInfo
          ? 'No passwords, licences, sessions or query history. Meta info included, sample values and all.'
          : 'No passwords, licences, sessions or query history are in that file.',
      });
    } catch (error) {
      toast.error({
        title: 'Export failed',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-3" data-testid="settings-export-panel">
      <div className="text-sm font-medium">Export</div>
      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Connections, passwords, licence keys, sessions and query history are never written
          to this file. Saved connections have their own export. Note that the meta-info
          group, if you tick it, includes cached sample values read from your columns.
        </span>
      </p>

      <SettingsGroupChecklist
        rows={rows}
        selected={selected}
        onToggle={toggle}
        idPrefix="settings-export"
      />

      <Button
        onClick={onExport}
        disabled={busy || selected.size === 0}
        data-testid="settings-export-button"
        className="gap-2"
      >
        <Download className="h-4 w-4" />
        {busy ? 'Exporting…' : 'Export settings…'}
      </Button>
    </section>
  );
}
